import { AlertController } from 'ionic-angular';
import { NurseCallService } from '../nurse-call.service';
import { MAKER_LIST } from '../../configs/maker.config';

export type NursecallInput = {
  ctlId: string;
  equipId: string;
  callNoCd?: string;
  roomCode: string;
  roomName: string;
  bedNo: string;
  memo?: string;
};

export type ColumnTypes = {
  key: string;
  text: string;
  checkValidate: boolean;
};

export type Maker = typeof MAKER_LIST[number];

export abstract class AbstractNursecallService {

  constructor(
    protected alertCtrl: AlertController,
    protected nurseCallService: NurseCallService,
  ) {
  }

  abstract placeholderText();

  abstract columns();

  abstract parse(params: { inputText: string }): NursecallInput[];

  abstract validate(params: { maker: Maker; rows: NursecallInput[] }): Promise<boolean>;

  showColumns() {
    return this.columns().filter((column) => column.isShowConfirm);
  }

  /**
   * 入力行毎の存在チェック
   */
  validateRows(rows: NursecallInput[], targetColumns: ColumnTypes[] = []): rows is NursecallInput[] {
    const errors = [];
    rows.forEach((row, index) => {
      targetColumns.forEach((column) => {
        if (!row[column.key]) {
          errors.push(`${index + 1}行目：${column.text}が未入力です。`);
        }
      });
    });

    if (errors.length > 0) {
      const alert = this.alertCtrl.create({
        title: '入力エラー',
        message: `未入力の行があります<br>${errors.join('<br>')}`,
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
            cssClass: 'confirm-button',
          }
        ],
        cssClass: 'custom-alert',
      })
      alert.present();
      return false;
    }

    return true;
  }

  validateDuplicated(rows: NursecallInput[], targetColumns: ColumnTypes[] = []): boolean {
    const duplicated = this.getDuplicatedIds(rows, targetColumns);
    if (duplicated.length > 0) {
      const alert = this.alertCtrl.create({
        title: '入力エラー',
        message: `重複があります<br>■重複している情報<br>${duplicated.join('<br>')}`,
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
            cssClass: 'confirm-button',
          }
        ],
        cssClass: 'custom-alert left-align-message start-align-items-message',
      })
      alert.present();
      return false;
    }
    return true;
  }

  async validateRegistered(maker: Maker, rows: NursecallInput[], targetColumns: ColumnTypes[] = []): Promise<boolean> {
    const registered = await this.getRegisteredIds(maker, rows, targetColumns);
    if (registered.length > 0) {
      const alert = this.alertCtrl.create({
        title: '入力エラー',
        message: `すでに登録済みのデータが<br>含まれています<br><br>${registered.join('<br>')}`,
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
            cssClass: 'confirm-button',
          }
        ],
        cssClass: 'custom-alert left-align-message start-align-items-message'
      })
      alert.present();
      return false;
    }
    return true;
  }

  /**
   * rowsのうち重複しているtargetColumnsのリストを取得する
   */
  getDuplicatedIds(rows: NursecallInput[], targetColumns: ColumnTypes[] = []): string[] {

    const checkedObj = {};
    const duplicatedObj = {};

    for (const row of rows) {
      // targetColumnsの値をキーとして重複をチェック
      for (const column of targetColumns) {
        const key = row[column.key];
        if (!key) {
          continue;
        }
        if (!checkedObj[column.key]) {
          checkedObj[column.key] = {};
        }
        if (!duplicatedObj[column.key]) {
          duplicatedObj[column.key] = {};
        }
        if (checkedObj[column.key][key]) {
          duplicatedObj[column.key][key] = true;
        } else {
          checkedObj[column.key][key] = true;
        }
      }
    }

    const duplicatedText = [];
    for (const column of targetColumns) {
      Object.keys(duplicatedObj[column.key]).forEach((value) => {
        duplicatedText.push(`${column.text}: ${value}`);
      });
    }

    return duplicatedText;

  }

  /**
   * 入力されたデータのうちすでに登録済みのリストを取得する
   */
  async getRegisteredIds(maker: Maker, rows: NursecallInput[], targetColumns: ColumnTypes[] = []): Promise<string[]> {

    if (!maker) return [];

    const { makerCode, productCode } = maker;

    // 同じメーカー、同じ製品のシリアルIDのリストを取得
    const res = await this.nurseCallService.loadAsPromise();
    const registeredNursecallList = res.data.callRoomList
      .filter(
        (nursecall) =>
          nursecall.makerCode === makerCode && nursecall.productCode === productCode
      )

    const duplicatedObj = {};

    for (const row of rows) {
      // targetColumnsの値をキーとして重複をチェック
      for (const column of targetColumns) {
        // registeredNursecallListに存在する場合は重複として扱う
        console.log({ key: column.key, row });
        const dup = registeredNursecallList.find((nursecall) => {
          console.log({ nursecall });
          return column.key in nursecall && nursecall[column.key] === row[column.key];
        });

        if (dup) {
          if (!duplicatedObj[dup._id]) {
            duplicatedObj[dup._id] = {};
          }
          duplicatedObj[dup._id][column.key] = row[column.key];
        }
      }
    }

    const duplicatedText = [];
    for (const column of targetColumns) {
      Object.keys(duplicatedObj).forEach((id) => {
        duplicatedText.push(`${column.text}: ${duplicatedObj[id][column.key]}`);
      });
    }

    return duplicatedText;
  }

}



// WEBPACK FOOTER //
// ./src/providers/nursecall/abstract-nursecall.service.ts